import { RatePlanCodeValidationRequest } from './../models/rate-plan-code-validation-request';
import { List } from 'immutable';
import { Injectable } from '@angular/core';
import { Headers, RequestOptions, ResponseContentType, Response } from '@angular/http';
import { OnyxEvent } from '../models/event';
import { Observable } from 'rxjs/Observable';
import { OnyxSummaryEvent } from '../models/event.summary';
import { Page } from '../../shared/models/page';
import { EventSearchParameters, EventSearchParamsDesc } from '../models/event-search-params';
import { ResponseUtil } from '../../shared/utils/response-util';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { BulkUploadResponse } from '../models/bulk-upload-response';
import { ResponseUtilService } from '../../shared/utils/response-util.service';
import { AppAuthHttpService } from '../../http/app.auth.http.service';
import { StaySummary } from '../models/stay-summary';
import { ValidationError } from '../models/validation-error';
import { RatePlanCodeReservationCounts } from '../models/rate-plan-code-reservation-counts';

@Injectable()
export class EventHttpService {
  private eventsGetUrl: string = this.environment.baseUrl + '/api/events';
  private eventSearchUrl: string = this.environment.baseUrl + '/api/events/search';
  private eventsAddUrl: string = this.environment.baseUrl + '/api/events';
  private eventsAddAgencyUrl: string = this.environment.baseUrl + '/api/events/agency';
  private eventsUpdateUrl: string = this.environment.baseUrl + '/api/events';
  private eventsUpdateAgencyUrl: string = this.environment.baseUrl + '/api/events/agency';
  private eventsUpdateIndicatorsUrl: string = this.environment.baseUrl + '/api/events/indicators';
  private eventsBulkUploadUrl: string = this.environment.baseUrl + '/api/events/bulkUpload';
  private eventReportExcelUrl: string = this.environment.baseUrl + '/api/events/report/excel ';
  private staySummaryUrl = this.environment.baseUrl + '/api/events/stay-summary';
  private readonly ratePlanCodeValidationUrl = this.environment.baseUrl + '/api/events/rate-plan-code-validation';

  constructor(private http: AppAuthHttpService,
              private environment: EnvironmentConfiguration,
              private responseUtilService: ResponseUtilService) {
  }

  private get jsonOptions(): RequestOptions {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({ headers: headers });
    return options;
  }

  private get fileOptions(): RequestOptions {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const options = new RequestOptions({
      headers: headers,
      responseType: ResponseContentType.Blob
    });
    return options;
  }

  search(searchParameters: EventSearchParameters, page: number, size: number): Observable<Response | Page<OnyxSummaryEvent>> {
    return this.http.post(`${this.eventSearchUrl}?page=${page}&size=${size}`, JSON.stringify(searchParameters.toJS()), this.jsonOptions)
      .map(response => {
        let eventsPage = response.json();
        eventsPage.content = List<OnyxSummaryEvent>(eventsPage.content);
        return eventsPage as Page<OnyxSummaryEvent>;
      })
      .catch(response => this.responseUtilService.handleError(response))
      .share();
  }

  get(eventId: number): Observable<OnyxEvent> {
    return this.http.get(`${this.eventsGetUrl}/${eventId}`)
      .map(response => response.json())
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  add(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.http.post(this.eventsAddUrl, JSON.stringify(newRec.toJS()), this.jsonOptions)
      .map(response => response.json())
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  addAsAgency(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.http.post(this.eventsAddAgencyUrl, JSON.stringify(newRec.toJS()), this.jsonOptions)
      .map(response => response.json())
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  update(updatedRec: OnyxEvent): Observable<OnyxEvent> {
    return this.http.put(this.eventsUpdateUrl, JSON.stringify(updatedRec.toJS()), this.jsonOptions)
      .map(response => response.json())
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  updateAsAgency(updatedRec: OnyxEvent): Observable<OnyxEvent> {
    return this.http.put(this.eventsUpdateAgencyUrl, JSON.stringify(updatedRec.toJS()), this.jsonOptions)
      .map(response => response.json())
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  delete(eventId: number): Observable<any> {
    return this.http.delete(`${this.eventsGetUrl}/${eventId}`)
      .map(response => response.json())
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  updateIndicators(updatedRec: OnyxEvent): Observable<OnyxEvent> {
    return this.http.put(this.eventsUpdateIndicatorsUrl, JSON.stringify(updatedRec.toJS()), this.jsonOptions)
      .map(response => response.json())
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  importEventsViaCsv(file: File): Observable<BulkUploadResponse> {
    let formData: FormData =  new FormData();
    formData.append('file', file);
    return this.http.post(`${this.eventsBulkUploadUrl}`, formData)
      .map(response => response.json())
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  downloadInvalidEventsCsv(fileId: number): Observable<any> {
    this.http.get(`${this.eventsBulkUploadUrl}` + `/` + fileId);
    return Observable.of(null);
  }

  getRecentlyEvents(searchParameters: EventSearchParameters): Observable<Page<OnyxSummaryEvent>> {
    return this.http.post(this.eventSearchUrl, JSON.stringify(searchParameters), this.jsonOptions)
      .map(response => response.json()).catch(ResponseUtil.unwrapErrorMessage).share();
  }

  exportEventExcel(searchParameters: EventSearchParamsDesc): Observable<any> {
    return this.http.post(this.eventReportExcelUrl, JSON.stringify(searchParameters), this.fileOptions)
      .map(res => new Blob([res.blob()], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      .catch(response => this.responseUtilService.handleError(response));
  }

  getStaySummary(event: OnyxEvent): Observable<StaySummary | Response> {
    return this.httpPost(this.staySummaryUrl, event);
  }

  getRatePlanCodeValidation(request: RatePlanCodeValidationRequest): Observable<ValidationError[] | Response> {
    return this.httpPost(this.ratePlanCodeValidationUrl, request);
  }

  getReservationCountForRatePlanCode(eventId: number, ratePlanCode: string): Observable<RatePlanCodeReservationCounts | Response> {
    const url = this.environment.baseUrl + `/api/events/${eventId}/rate-plan-codes/${ratePlanCode}/reservation-counts`;
    return this.http.get(url)
      .map(response => new RatePlanCodeReservationCounts(response.json()))
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  private httpPost(url: string, request: any): Observable<any> {
    const options = new RequestOptions({ headers: new Headers({ 'Content-Type': 'application/json' }) });
    return this.http.post(url, JSON.stringify(request), options)
      .map(response => response.json())
      .catch(error => this.responseUtilService.handleError(error))
      .share();
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/service/event.http.service.ts